import { DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { ImageType } from '@kp/shared/image/image.bloc';
import { Feature, Product } from '@ktypes/enums';
import { InviteEmail, JsonObject, NavItem, NavItemType } from '@ktypes/models';
import { envVariables } from '../../environments/environment-variables';

export interface QuickAction {
  queryParams: JsonObject<string>;
  returnUrl: string | boolean;
  route: string;
  svgUrl: string;
  text: string;
}

export class Constants {
  static rootDomain = `${envVariables.PRODUCT === Product.resourceful ? 'resourceful' : 'purposeful'}.kumanu.com`;
  static focusRefKey = '4cUELRf7xSuxjKuExOf305';
  static lifeQuestKey = 'life_purpose_quest_v1';
  static workQuestKey = 'work_purpose_quest_v1';
  static intentionRefKey = '6911QMbY050RL30bCkPinH';
  static journalRefKey = '4RrHp0hbJLuOtmtHxwp66E';
  static feedbackLinkBase =
    'https://www.surveygizmo.com/s3/5029608/Purposeful-Content-Feedback-Form?title={{cardTitle}}';
  static purposefulStaticPlayStoreUrl = 'https://play.google.com/store/apps/details?id=com.kumanu.purposeful';
  static ssoLoginUrlTemplate = `$authHost/oauth2/authorize?identity_provider=$provider&redirect_uri=$kumanuHost&response_type=$response_type&client_id=$clientId&state=$state&scope=email%20openid%20phone%20profile`;
  static ssoLogoutUrlTemplate = '$authHost/logout?client_id=$clientId&logout_uri=$kumanuLogout';
  static ssoLogoutAndReloginUrlTemplate =
    '$authHost/logout?identity_provider=$provider&redirect_uri=$kumanuHost&response_type=$response_type&client_id=$clientId&state=$state&scope=email%20openid%20phone%20profile';
  // Dynamic link url parameters: https://firebase.google.com/docs/dynamic-links/create-manually
  static mobileTransferLinkBase = 'https://kumanu.page.link/?link=';
  static mobileTransferDynamicLinkTemplate = '$windowLocationOrigin/$groupCode?groupCode=$groupCode&userId=$userId';
  static mobileTransferDynamicLinkQueryParamsTemplate =
    '&apn=com.kumanu.purposeful&isi=1457841328&ibi=com.kumanu.purposeful&st=$socialTitle&sd=$socialCTA';
  static googleMapsDirectionsLinkBase = 'https://www.google.com/maps/dir/?api=1&';
  static searchStrings = [
    'Try "Purpose"',
    'Try "Mindfulness"',
    'Try "Gratitude"',
    'Try "Meditations"',
    'Try "Energy"',
    'Try "Sleep"',
    'Try "Work"',
    'Try "Relationships"',
    'Try "Stress"',
    'Try "Reflections"',
    'Try "Life balance"',
    'Try "Resilience"',
    'Try "Family"',
    'Try "Performance"',
    'Try "Leadership"',
    'Try "Productivity"',
    'Try "Community"',
    'Try "Health"',
    'Try "Communication"',
    'Try "Transcendence"',
    'Try "Helping others"',
    'Try "Self care"',
  ];
  static dialogueSystemKey = 'purposeful_web';
  static imgTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  static states = [
    'AK',
    'AL',
    'AR',
    'AS',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'GU',
    'HI',
    'IA',
    'ID',
    'IL',
    'IN',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MI',
    'MN',
    'MO',
    'MP',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'PR',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UM',
    'UT',
    'VA',
    'VI',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
  ];

  static supportedBrowsers = [
    { name: 'MS-Edge', version: 80, os: ['Windows', 'Mac'], matchExact: false, recommend: false },
    { name: 'MS-Edge', version: 44, os: ['Windows', 'Mac', 'iOS', 'Android'], matchExact: true, recommend: false },
    { name: 'MS-Edge-Chromium', version: 80, os: [], matchExact: false, recommend: false },
    {
      displayName: 'Edge',
      name: 'MS-Edge-Chromium',
      version: 44,
      os: [],
      matchExact: true,
      recommend: true,
      icon: '/assets/icons/browsers/edge.png',
      url: 'https://www.microsoft.com/en-us/edge',
    },
    {
      displayName: 'Chrome',
      name: 'Chrome',
      version: 0,
      os: [],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/chrome.png',
      url: 'https://www.google.com/chrome/',
    },
    {
      displayName: 'Firefox',
      name: 'Firefox',
      version: 0,
      os: [],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/firefox.png',
      url: 'https://www.mozilla.org/en-US/firefox/new/',
    },
    { displayName: 'Internet Explorer', name: 'IE', version: 11, os: ['Windows'], matchExact: false, recommend: false },
    {
      displayName: 'Safari',
      name: 'Safari',
      version: 12,
      os: ['Mac', 'iOS'],
      matchExact: false,
      recommend: true,
      icon: '/assets/icons/browsers/safari.png',
      url: 'https://support.apple.com/downloads/safari',
    },
  ];

  static platformName = 'Web';

  static appSpecificRoutes = {
    [Product.resourceful]: ['/resources'],
    [Product.purposeful]: [
      '/purposeful-days',
      '/explore',
      '/discover',
      '/me',
      '/welcome/mobile',
      '/mobile_app_info',
      '/progress',
      '/reminders',
      '/today',
      '/welcome/mobile-app/interstitial',
      '/welcome/mobile-app/interstitial/mobile',
    ],
  };
  static navItemList: NavItem[] = [
    {
      link: '/today',
      icon: '/assets/icons/icons-nav-today.svg',
      type: NavItemType.standard,
      title: 'Today',
      displayOrder: { purposeful: { core: 10 } },
    },
    {
      link: '/discover',
      icon: '/assets/icons/icons-nav-discover.svg',
      type: NavItemType.standard,
      title: 'Discover',
      displayOrder: { purposeful: { core: 20 } },
    },
    {
      link: '',
      icon: '',
      type: NavItemType.quickActions,
      displayOrder: { purposeful: { core: 30 } },
    },
    {
      link: '/progress',
      icon: '/assets/icons/icons-nav-progress.svg',
      type: NavItemType.standard,
      title: 'Progress',
      displayOrder: { purposeful: { core: 40 } },
    },
    {
      link: '/me',
      icon: '/assets/icons/icons-nav-me.svg',
      type: NavItemType.standard,
      imageType: ImageType.user_profile,
      title: 'Me',
      displayOrder: { purposeful: { core: 50 } },
    },
    {
      link: '/resources',
      icon: '/assets/icons/resources.svg',
      type: NavItemType.standard,
      title: 'Resources',
      displayOrder: { resourceful: { side: 10 } },
      external: false,
    },
    {
      link: '/cards/saved',
      icon: '/assets/icons/save-filled.svg',
      type: NavItemType.standard,
      title: 'Saved',
      displayOrder: { purposeful: { side: 10 }, resourceful: { side: 20 } },
      external: false,
    },
    {
      link: '/report',
      icon: '/assets/icons/report-new.svg',
      type: NavItemType.standard,
      title: 'Report',
      displayOrder: { purposeful: { side: 20 }, resourceful: { side: 30 } },
      external: false,
      feature: Feature.RESOURCEFUL,
    },
    {
      link: '/reminders',
      icon: '/assets/icons/reminder.svg',
      type: NavItemType.standard,
      title: 'Notifications',
      displayOrder: { purposeful: { side: 30 } },
      external: false,
    },
    {
      link: '/invite',
      icon: '/assets/icons/mail.svg',
      type: NavItemType.invites,
      title: 'Invite',
      displayOrder: { purposeful: { side: 30 } },
      external: false,
    },
    {
      link: '/settings',
      icon: '/assets/icons/gear.svg',
      type: NavItemType.standard,
      title: 'Settings',
      displayOrder: { purposeful: { side: 40 }, resourceful: { side: 40 } },
      external: false,
    },
    {
      link: '/help',
      icon: '/assets/icons/question.svg',
      type: NavItemType.standard,
      title: 'Help',
      displayOrder: { purposeful: { side: 50 }, resourceful: { side: 50 } },
      external: false,
    },
    {
      link: '/feedback',
      icon: '/assets/icons/feedback.svg',
      type: NavItemType.standard,
      title: 'Feedback',
      displayOrder: { purposeful: { side: 60 }, resourceful: { side: 60 } },
      external: false,
    },
    {
      link: 'https://www.kumanu.com/about-us/',
      icon: '/assets/icons/about.svg',
      type: NavItemType.standard,
      title: 'About Kumanu',
      displayOrder: { purposeful: { side: 70 } },
      external: true,
    },
    {
      link: '/mobile_app_info',
      icon: '/assets/icons/mobile-icon.svg',
      type: NavItemType.standard,
      title: 'Purposeful Mobile App',
      displayOrder: { purposeful: { side: 80 } },
      external: false,
    },
  ];

  static quickActions: QuickAction[] = [
    {
      queryParams: { logicKey: DialogueLogicKey.dialogue_daily_reflection_v1 },
      returnUrl: true,
      route: '/dialogue',
      svgUrl: '/assets/icons/smiley-grin-reversed.svg',
      text: 'Check in',
    },
    {
      queryParams: {},
      returnUrl: false,
      route: '/cards/take-action',
      svgUrl: '/assets/icons/checkmark-empty-circle.svg',
      text: 'Log an action',
    },
    {
      queryParams: {
        logicKey: DialogueLogicKey.templated_dialogue,
        refKey: Constants.intentionRefKey,
      },
      returnUrl: true,
      route: '/dialogue',
      svgUrl: '/assets/icons/compass.svg',
      text: 'Set an intention',
    },
    {
      queryParams: { action: 'add_photo' },
      returnUrl: false,
      route: '/me/photos',
      svgUrl: '/assets/icons/mountain-image-summit-flag.svg',
      text: 'Add a photo',
    },
    {
      queryParams: { logicKey: DialogueLogicKey.templated_dialogue, refKey: Constants.journalRefKey },
      returnUrl: true,
      route: '/dialogue',
      svgUrl: '/assets/icons/journal.svg',
      text: 'Journal',
    },
  ];

  static actionChoice = {
    clickToCreateAccount: 'click to create account',
    clickNotNow: 'click Not Now',
    clickX: 'click x',
    clickOutsideModal: 'click outside modal',
    clickBannerToCreateAccount: 'click banner to create account',
  };

  static inviteEmails: InviteEmail = {
    coworker: {
      subject: 'Get started with Purposeful!',
      body: 'Through our organization, we have access to a wellbeing app called Purposeful — which helps you make progress in areas you’re trying to improve, and build positive habits. It’s free for us to use, so give it a try! Get started by following the link below.',
    },
    friend: {
      subject: 'Get the Purposeful app free with my link!',
      body: 'Through my organization, I have access to a wellbeing app called Purposeful — it supports you in making progress in areas you’re trying to improve, and also helps with building positive habits. Because I have access, I can share it with you for free! Follow the link below to check it out.',
    },
    spouse: {
      subject: 'Join me in Purposeful!',
      body: 'Through my organization, I was given access to a wellbeing app called Purposeful — it helps me build positive habits, and make progress in areas where I’m trying to improve. With my link you can also get free access. Follow the link below to get started.',
    },
  };

  get randomSearchText() {
    return Constants.searchStrings[Math.floor(Math.random() * Constants.searchStrings.length)];
  }

  static get shuffledBrowserArray(): any[] {
    return this.shuffleArray(Constants.supportedBrowsers);
  }

  static shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
}

export const recommendedText = {
  default: 'Resources, Tips, Quests, and more...',
  resources: 'Resources, Tips, Quests, and more...',
};

export const PREDEFINED_SEARCH_TITLE = 'What would you like help with?';
